import { FC, PropsWithChildren, Reducer, createContext, useContext, useReducer } from 'react';
import { UserType } from '../../lib/video/types';
import {
  VideoCallActionTypes,
  VideoCallActions,
  VideoCallContextValue,
  VideoCallState,
} from './types';

const VideoCallContext = createContext<VideoCallContextValue | undefined>(undefined);

const initialState: VideoCallState = {
  meetingInfo: null,
  videoPreview: null,
  mediaSettings: {
    micEnabled: true,
    videoEnabled: true,
  },
};

export const videoCallReducer: Reducer<VideoCallState, VideoCallActionTypes> = (
  prevState,
  action
) => {
  const { type, payload } = action;

  switch (type) {
    case VideoCallActions.ChimeSetCallInfo:
      return {
        ...prevState,
        meetingInfo: payload,
      };
    case VideoCallActions.SetVideoPreview:
      return {
        ...prevState,
        videoPreview: payload,
      };
    case VideoCallActions.ToggleMic:
      return {
        ...prevState,
        mediaSettings: {
          ...prevState.mediaSettings,
          micEnabled: !prevState.mediaSettings.micEnabled,
        },
      };
    case VideoCallActions.ToggleVideo:
      return {
        ...prevState,
        mediaSettings: {
          ...prevState.mediaSettings,
          videoEnabled: !prevState.mediaSettings.videoEnabled,
        },
      };

    default:
      return prevState;
  }
};

export const VideoCallProvider: FC<PropsWithChildren> = ({ children }) => {
  const [videoCallState, dispatchVideoCall] = useReducer(videoCallReducer, initialState);

  function getParticipantNameBy(userType: UserType) {
    if (userType === UserType.Provider) {
      return videoCallState.meetingInfo?.meetingAttendees?.provider.name;
    } else if (userType === UserType.Member) {
      return videoCallState.meetingInfo?.meetingAttendees?.member.name;
    }

    return undefined;
  }

  return (
    <VideoCallContext.Provider value={{ videoCallState, dispatchVideoCall, getParticipantNameBy }}>
      {children}
    </VideoCallContext.Provider>
  );
};

export const useVideoCall = () => {
  const videoCallContext = useContext(VideoCallContext);

  if (!videoCallContext) {
    throw new Error('videoCallContext must be used within VideoCallContext.Provider');
  }

  return videoCallContext;
};
